// React and PropTypes
import React, { memo } from "react";
import PropTypes from "prop-types";

// Toddle Design System components
import { Button } from "@toddle-edu/ds-web";

// Hooks
import { useI18n } from "Hooks";

// Styles
import classes from "./FilterActions.scss";

/**
 * @component FilterActions
 * @description Renders the action buttons (Cancel, Apply) and displays the count of applied filters.
 *
 * @param {object} props - Component props.
 * @param {number} props.totalAppliedFilters - The total count of filters that have changed from their default state.
 * @param {Function} props.handleApplyFilters - Callback function triggered when the Apply button is clicked.
 * @param {Function} props.handleClose - Callback function triggered when the Cancel button is clicked.
 * @param {string} props.testId - Base test ID for targeting elements.
 * @param {Function} [props.resetFilters] - Callback function to reset filters to their default state.
 *
 * @example
 * const handleApply = () => console.log('Apply clicked');
 * const handleClose = () => console.log('Cancel clicked');
 *
 * <FilterActions
 *   totalAppliedFilters={3}
 *   handleApplyFilters={handleApply}
 *   handleClose={handleClose}
 *   testId="my-filter"
 *   resetFilters={() => console.log('Reset clicked')}
 * />
 */
const FilterActions = props => {
  const {
    totalAppliedFilters,
    handleApplyFilters,
    testId,
    handleClose,
    resetFilters,
  } = props;

  const { t } = useI18n(["common"]);

  return (
    <div className={classes.container}>
      {totalAppliedFilters ? (
        <Button
          onClick={resetFilters}
          type="inline"
          variant="progressive"
          testId={`${testId}-filterActions-reset`}
        >
          {t("common:reset_filter_plural")}
        </Button>
      ) : null}
      <div className={classes.rightContainer}>
        <Button
          type="outlined"
          variant="neutral"
          testId={`${testId}-filterActions-cancel`}
          onClick={handleClose}
        >
          {t("common:cancel")}
        </Button>
        <Button
          onClick={handleApplyFilters}
          type="fill"
          variant="progressive"
          testId={`${testId}-filterActions-apply`}
        >
          {t("common:apply")}
        </Button>
      </div>
    </div>
  );
};

FilterActions.propTypes = {
  totalAppliedFilters: PropTypes.number.isRequired,
  handleApplyFilters: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  resetFilters: PropTypes.func,
};

export default memo(FilterActions);
